import {
  marketplaceOptions,
  type ListingRecord,
  type PaperKingdomProject,
} from "../domain/schema";

export type MarketplaceName = PaperKingdomProject["marketplace"];

export interface MarketplaceGuide {
  name: MarketplaceName;
  shortName: string;
  plainDescription: string;
  firstOutcome: string;
  requirementsHint: string;
  formatOptions: PaperKingdomProject["format"][];
  listingName: string;
  supportsAPlus: boolean;
  usesKdpPrintSetup: boolean;
  accountStepLabel: string;
  accountStepHint: string;
  optionalMarketingName: string;
}

export interface SellingKitGuide {
  titleEditable: boolean;
  searchCount: number;
  searchLabel: string;
  searchHint: string;
  visualCount: number;
  visualLabel: string;
  visualHint: string;
  showSeoFields: boolean;
  descriptionFocus: string;
  uploadSteps: string[];
}

export const marketplaceGuides: Record<MarketplaceName, MarketplaceGuide> = {
  "Amazon KDP": {
    name: "Amazon KDP",
    shortName: "Amazon KDP",
    plainDescription:
      "Publish printed books that Amazon can manufacture after a customer orders.",
    firstOutcome:
      "You will prepare an interior PDF, a full print-cover file, and an accurate Amazon book listing.",
    requirementsHint: "Check the current KDP requirements immediately before publishing.",
    formatOptions: [
      "Low-content paperback",
      "Guided journal",
      "Activity book",
      "Workbook",
      "Planner",
    ],
    listingName: "Amazon book listing",
    supportsAPlus: true,
    usesKdpPrintSetup: true,
    accountStepLabel: "KDP account ready",
    accountStepHint: "Check this after the KDP account, payment details, and tax information are ready.",
    optionalMarketingName: "Amazon extras and reader follow-up",
  },
  Etsy: {
    name: "Etsy",
    shortName: "Etsy",
    plainDescription:
      "Sell a downloadable file or a product fulfilled through the process you choose in an Etsy shop.",
    firstOutcome:
      "You will prepare the customer file, clear preview images, simple usage terms, and an accurate Etsy listing.",
    requirementsHint: "Check the current Etsy requirements immediately before publishing.",
    formatOptions: [
      "Printable PDF",
      "Workbook",
      "Planner",
      "Activity book",
      "Guided journal",
    ],
    listingName: "Etsy product listing",
    supportsAPlus: false,
    usesKdpPrintSetup: false,
    accountStepLabel: "Etsy shop ready",
    accountStepHint: "Check this after the shop, payment details, policies, and public page are ready.",
    optionalMarketingName: "Optional buyer follow-up",
  },
  "Shopify / direct": {
    name: "Shopify / direct",
    shortName: "Shopify or your own site",
    plainDescription:
      "Sell a download or physical product from a store you control.",
    firstOutcome:
      "You will prepare the customer files or fulfilment instructions, product images, and a clear store product page.",
    requirementsHint: "Test your current store, checkout, delivery, and support process before publishing.",
    formatOptions: [
      "Printable PDF",
      "Guided journal",
      "Activity book",
      "Workbook",
      "Planner",
      "Low-content paperback",
    ],
    listingName: "Store product page",
    supportsAPlus: false,
    usesKdpPrintSetup: false,
    accountStepLabel: "Store checkout ready",
    accountStepHint: "Check this after checkout, delivery, support, and payment settings have been tested.",
    optionalMarketingName: "Optional customer follow-up",
  },
  "Creative Market": {
    name: "Creative Market",
    shortName: "Creative Market",
    plainDescription:
      "Sell a finished digital design product with clear previews, files, and licence information.",
    firstOutcome:
      "You will prepare an organized download, preview images, a simple file guide, licence notes, and a truthful product listing.",
    requirementsHint: "Check the current Creative Market requirements immediately before publishing.",
    formatOptions: [
      "Printable PDF",
      "Workbook",
      "Planner",
      "Activity book",
    ],
    listingName: "Creative Market listing",
    supportsAPlus: false,
    usesKdpPrintSetup: false,
    accountStepLabel: "Creative Market shop ready",
    accountStepHint: "Check this after the shop profile, payment details, licence information, and support details are ready.",
    optionalMarketingName: "Optional customer follow-up",
  },
  "Multi-channel": {
    name: "Multi-channel",
    shortName: "More than one channel",
    plainDescription:
      "Prepare one reliable product source, then adapt its files and listing for each place you sell.",
    firstOutcome:
      "This older project setting is no longer offered for new projects. Use one project per sales channel so files and listings cannot be mixed.",
    requirementsHint: "Choose one sales channel for each project before publishing.",
    formatOptions: [
      "Printable PDF",
      "Guided journal",
      "Activity book",
      "Workbook",
      "Planner",
      "Low-content paperback",
    ],
    listingName: "Master product listing",
    supportsAPlus: false,
    usesKdpPrintSetup: false,
    accountStepLabel: "Sales accounts ready",
    accountStepHint: "Check this only when each chosen channel has a working account, checkout, and delivery path.",
    optionalMarketingName: "Optional cross-channel follow-up",
  },
};

export const sellingKitGuides: Record<MarketplaceName, SellingKitGuide> = {
  "Amazon KDP": {
    titleEditable: false,
    searchCount: 7,
    searchLabel: "KDP keyword phrases",
    searchHint: "Paper Kingdom creates all seven phrases. Check each phrase in Amazon before publishing.",
    visualCount: 3,
    visualLabel: "Three promotion image ideas",
    visualHint: "Use the real cover and inside pages. A+ Content can be prepared after the book is live.",
    showSeoFields: false,
    descriptionFocus: "Explain the book's contents, intended reader, use, and limitations without unsupported claims.",
    uploadSteps: [
      "Enter the exact title, subtitle, contributor, and description from this project.",
      "Add the seven keyword phrases and choose three accurate categories from the current KDP choices.",
      "Upload the inside PDF and final cover, then complete rights, pricing, and distribution details.",
      "Use KDP Previewer, correct any problem, save the draft, and publish only when everything matches.",
      "After the book is live, add A+ Content only if it gives the buyer useful extra information.",
    ],
  },
  Etsy: {
    titleEditable: true,
    searchCount: 13,
    searchLabel: "Etsy tags",
    searchHint: "Paper Kingdom creates thirteen distinct tag ideas. Confirm they accurately describe the finished item.",
    visualCount: 5,
    visualLabel: "Five listing-image ideas",
    visualHint: "Show the real download, what is included, how it is used, and anything the buyer will not receive.",
    showSeoFields: false,
    descriptionFocus: "State that the item is digital when applicable, what files are included, how to use them, and important limitations.",
    uploadSteps: [
      "Use the clear listing title, description, and thirteen relevant tags from this selling kit.",
      "Choose the closest current Etsy category and complete only the attributes that truly apply.",
      "Upload the real customer files and listing images, then check that every promise matches the download.",
      "Set the price, shop section, renewal, and policies in the live shop.",
      "Preview the listing on desktop and mobile, test the purchase or download path, then publish.",
    ],
  },
  "Shopify / direct": {
    titleEditable: true,
    searchCount: 5,
    searchLabel: "search and SEO phrases",
    searchHint: "Use these naturally in the page. They are writing guidance, not guaranteed traffic terms.",
    visualCount: 5,
    visualLabel: "Five product-media ideas",
    visualHint: "Show what the customer receives, scale or page examples, use, important details, and limitations.",
    showSeoFields: true,
    descriptionFocus: "Explain the product, included files or fulfilment, specifications, uses, benefits supported by facts, and limitations.",
    uploadSteps: [
      "Create the product with the selling-kit title, description, media, price, and delivery details.",
      "Add the search-engine title and description, then check the product URL and search preview.",
      "Connect the correct customer file or fulfilment process and test delivery.",
      "Test the product page, checkout, confirmation, taxes, payment, and support information.",
      "Publish the product, complete a test order, and correct any customer-facing problem.",
    ],
  },
  "Creative Market": {
    titleEditable: true,
    searchCount: 8,
    searchLabel: "Creative Market tags",
    searchHint: "Use a mix of accurate broad and specific tags. Do not add near-duplicates.",
    visualCount: 5,
    visualLabel: "Five product-screenshot ideas",
    visualHint: "Show the real files, included pages, important features, compatibility, and licence-relevant limitations.",
    showSeoFields: false,
    descriptionFocus: "List what is included, file types, software or compatibility requirements, use, and what is not included.",
    uploadSteps: [
      "Use the selling-kit title, detailed description, and relevant tags.",
      "Choose the closest current category and complete the product specifications and AI-content field accurately.",
      "Upload the organized product ZIP and a useful read-me with required compatibility and asset information.",
      "Upload screenshots that show the real product and match everything promised in the description.",
      "Check the live preview, price, licence presentation, files, and support details before making it available for sale.",
    ],
  },
  "Multi-channel": {
    titleEditable: true,
    searchCount: 5,
    searchLabel: "channel-specific search phrases",
    searchHint: "Use a separate project for each channel before relying on these phrases.",
    visualCount: 5,
    visualLabel: "Five product-image ideas",
    visualHint: "Adapt the images and copy separately for each channel.",
    showSeoFields: false,
    descriptionFocus: "Explain the product and delivery without mixing incompatible channel requirements.",
    uploadSteps: [
      "Choose one sales channel and create a separate project for it.",
      "Use that channel's listing, files, images, account settings, and live requirements.",
    ],
  },
};

export const beginnerMarketplaceOptions = marketplaceOptions.filter(
  (marketplace) => marketplace !== "Multi-channel",
);

export const formatGuidance: Record<
  PaperKingdomProject["format"],
  { plainName: string; bestFor: string; example: string }
> = {
  "Low-content paperback": {
    plainName: "Simple printed book",
    bestFor: "Repeated pages such as logs, notebooks, or simple trackers.",
    example: "A printed maintenance log with the same entry page repeated.",
  },
  "Guided journal": {
    plainName: "Guided journal",
    bestFor: "Reflection with questions, writing space, and a repeated routine.",
    example: "A daily journal that asks the reader what happened and what to do next.",
  },
  "Activity book": {
    plainName: "Activity book",
    bestFor: "Exercises, puzzles, challenges, or hands-on learning.",
    example: "A family book of simple home-safety activities.",
  },
  Workbook: {
    plainName: "Step-by-step workbook",
    bestFor: "Teaching a practical process with checklists and worksheets.",
    example: "A leak-check workbook that helps a homeowner inspect and record a problem.",
  },
  Planner: {
    plainName: "Planner",
    bestFor: "Dates, schedules, recurring tasks, and future planning.",
    example: "A yearly home-maintenance planner.",
  },
  "Printable PDF": {
    plainName: "Downloadable printable",
    bestFor: "Pages the customer downloads and prints at home.",
    example: "A printable emergency plumbing checklist.",
  },
};

export const recommendedTrimSizes: Record<PaperKingdomProject["format"], string> = {
  "Low-content paperback": "6 × 9 in",
  "Guided journal": "6 × 9 in",
  "Activity book": "8.5 × 11 in",
  Workbook: "8.5 × 11 in",
  Planner: "8 × 10 in",
  "Printable PDF": "8.5 × 11 in",
};

export function getMarketplaceGuide(marketplace: MarketplaceName): MarketplaceGuide {
  return marketplaceGuides[marketplace];
}

export function getSellingKitGuide(marketplace: MarketplaceName): SellingKitGuide {
  return sellingKitGuides[marketplace];
}

export function getPrimaryFileLabel(project: PaperKingdomProject): string {
  if (getMarketplaceGuide(project.marketplace).usesKdpPrintSetup) return "Inside PDF";
  if (project.marketplace === "Creative Market") return "Download package";
  if (project.marketplace === "Multi-channel") return "Master download package";
  if (
    project.marketplace === "Shopify / direct" &&
    project.format === "Low-content paperback"
  ) {
    return "Print production package";
  }
  return "Customer PDF";
}

export function getChannelDelivery(project: PaperKingdomProject): string {
  if (getMarketplaceGuide(project.marketplace).usesKdpPrintSetup) {
    if (!project.production.primaryFileCreated && !project.production.coverPdfCreated) return "";
    return [
      project.production.primaryFileCreated ? "Inside PDF created" : "",
      project.production.primaryFileCreated
        ? `${project.production.plannedPageCount} pages`
        : "",
      project.production.coverPdfCreated ? "Cover preview created" : "",
    ].filter(Boolean).join("; ");
  }
  const legacyDelivery = project.production.channelDelivery[project.marketplace]?.trim();
  if (legacyDelivery) return legacyDelivery;
  if (!project.production.primaryFileCreated) return "";
  return `${getPrimaryFileLabel(project)} created`;
}

export function getChannelListing(
  project: PaperKingdomProject,
  bookId: string,
): ListingRecord | undefined {
  const channelListing =
    project.launchGrowth.listingsByMarketplace[project.marketplace]?.[bookId];
  if (channelListing) return channelListing;
  return project.marketplace === "Amazon KDP"
    ? project.launchGrowth.listings[bookId]
    : undefined;
}

export function getChannelAccountReady(project: PaperKingdomProject): boolean {
  const saved = project.launchGrowth.channelAccountReady[project.marketplace];
  if (typeof saved === "boolean") return saved;
  return project.marketplace === "Amazon KDP"
    ? project.launchGrowth.authorCentralComplete
    : false;
}

export function getBeginnerLaunchSequence(marketplace: MarketplaceName): string[] {
  const guide = getMarketplaceGuide(marketplace);
  return [
    "Finish and test the customer files",
    `Prepare the ${guide.listingName}`,
    "Check rights and current marketplace requirements",
    `Publish on ${guide.shortName}`,
    "Review the live product and correct any problem",
  ];
}

export function isPromptRelevantToMarketplace(
  category: string,
  marketplace: MarketplaceName,
): boolean {
  if (category === "A+ Content") return marketplaceGuides[marketplace].supportsAPlus;
  return true;
}

const formatLayoutRecommendations: Record<PaperKingdomProject["format"], string[]> = {
  "Low-content paperback": ["orientation", "daily-entry", "tracker", "closing-review"],
  "Guided journal": ["orientation", "guided-reflection", "daily-entry", "weekly-review", "closing-review"],
  "Activity book": ["orientation", "section-opener", "challenge", "closing-review"],
  Workbook: ["orientation", "worksheet", "checklist", "closing-review"],
  Planner: ["orientation", "monthly-map", "daily-entry", "weekly-review"],
  "Printable PDF": ["orientation", "worksheet", "checklist", "closing-review"],
};

const formatModuleRecommendations: Record<PaperKingdomProject["format"], string[]> = {
  "Low-content paperback": ["daily-check-in", "habit-tracker", "closing-review"],
  "Guided journal": ["daily-check-in", "guided-journal", "weekly-review", "brain-dump", "closing-review"],
  "Activity book": ["challenge-page", "guided-journal", "closing-review"],
  Workbook: ["project-planner", "decision-page", "resource-log", "closing-review"],
  Planner: ["daily-check-in", "weekly-review", "monthly-map", "project-planner"],
  "Printable PDF": ["project-planner", "decision-page", "resource-log", "closing-review"],
};

export function recommendedLayoutIds(format: PaperKingdomProject["format"]): string[] {
  return formatLayoutRecommendations[format];
}

export function recommendedModuleIds(format: PaperKingdomProject["format"]): string[] {
  return formatModuleRecommendations[format];
}

export function getPreflightChecks(project: PaperKingdomProject): string[] {
  if (getMarketplaceGuide(project.marketplace).usesKdpPrintSetup) {
    return [
      "Every text, font, image, and template has a recorded source and usage right",
      "AI-created work and the required disclosure decision are recorded",
    ];
  }

  return [
    "The download or fulfilment method is confirmed for this sales channel",
    "Preview images show the real product without inventing features",
    "The delivery package has clear filenames and a short read-me",
    "Every text, font, image, and template has a recorded source and usage right",
    "AI-created work and the required disclosure decision are recorded",
    "The final customer file opens correctly and was reviewed page by page",
    "A test purchase or customer-file download was completed successfully",
  ];
}
